import React from "react";
import { Box, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, TextField, Typography } from "@mui/material";
import { Colors } from "../../../../styles/theme";

export function getDefaultObjectiveValues() {
  return {
    activity: {
      unaided: {
        dist: { od: "", os: "", ou: "" },
        near: { od: "", os: "", ou: "" },
      },
      aided: {
        type: "", // 'specs' | 'cls' (no default selected)
        dist: { od: "", os: "", ou: "" },
        near: { od: "", os: "", ou: "" },
      },
    },
    coverTest: {
      dist: {
        unil: { h: "", hDir: "", v: "", vDir: "" }, // hDir: 'bo'|'bi', vDir: 'bd'|'bu' (no default selected)
        ortho: false,
        ns: false,
        alt: { h: "", hDir: "", v: "", vDir: "" },
      },
      near: {
        unil: { h: "", hDir: "", v: "", vDir: "" },
        ortho: false,
        ns: false,
        alt: { h: "", hDir: "", v: "", vDir: "" },
      },
    },
    // Additional fields row
    npc: "",
    stereopsis: { method: "", d: "", n: "" },
    // Color and additional tests
    color: { ishihara: false, od: "", os: "", both: false },
    tests: {
      amp: { od: "", os: "" },
      fields: { od: "", os: "" },
      amsler: { od: "", os: "" },
    },
    // Practitioner free-form notes
    notes: "",
  };
}

function Title({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: Colors.dim_grey, mb: 1 }}>
      {children}
    </Typography>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1.5, mt: 2 }}>
      {children}
    </Typography>
  );
}

function ActivityGrid({ label, value, onChange, showAidedRadios }) {
  const handleChange = (section, eye) => (e) => {
    const next = {
      ...value,
      [section]: { ...value[section], [eye]: e.target.value },
    };
    onChange?.(next);
  };

  const handleAidedTypeChange = (e) => {
    onChange?.({ ...value, type: e.target.value });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{label}</Typography>
        {showAidedRadios && (
          <RadioGroup row value={value?.type ?? ""} onChange={handleAidedTypeChange}>
            <FormControlLabel value="specs" control={<Radio size="small" />} label="Specs" />
            <FormControlLabel value="cls" control={<Radio size="small" />} label="CLs" />
          </RadioGroup>
        )}
      </Box>

      {/* Header row */}
      <Grid container spacing={1} sx={{ mb: 0.5 }}>
        <Grid item xs={3}></Grid>
        <Grid item xs={3}><Typography variant="caption" sx={{ color: Colors.dim_grey }}>OD</Typography></Grid>
        <Grid item xs={3}><Typography variant="caption" sx={{ color: Colors.dim_grey }}>OS</Typography></Grid>
        <Grid item xs={3}><Typography variant="caption" sx={{ color: Colors.dim_grey }}>OU</Typography></Grid>
      </Grid>

      {/* Dist row */}
      <Grid container spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Grid item xs={3}><Typography variant="body2">Dist</Typography></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.dist?.od || ""} onChange={handleChange("dist", "od")} /></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.dist?.os || ""} onChange={handleChange("dist", "os")} /></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.dist?.ou || ""} onChange={handleChange("dist", "ou")} /></Grid>
      </Grid>

      {/* Near row */}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={3}><Typography variant="body2">Near</Typography></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.near?.od || ""} onChange={handleChange("near", "od")} /></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.near?.os || ""} onChange={handleChange("near", "os")} /></Grid>
        <Grid item xs={3}><TextField size="small" fullWidth value={value?.near?.ou || ""} onChange={handleChange("near", "ou")} /></Grid>
      </Grid>
    </Box>
  );
}

export default function ObjectiveTab({ value, onChange }) {
  const v = value || getDefaultObjectiveValues();

  const setActivitySection = (section, next) => {
    const updated = {
      ...v,
      activity: {
        ...v.activity,
        [section]: next,
      },
    };
    onChange?.(updated);
  };

  const setRootField = (key, val) => onChange?.({ ...v, [key]: val });
  const setStereo = (key, val) => onChange?.({ ...v, stereopsis: { ...(v.stereopsis || {}), [key]: val } });

  return (
    <Box>
      <Grid container spacing={2}>
        {/* VISUAL ACUITY */}
        <Grid item xs={12}>
          <SectionHeader>Visual Acuity</SectionHeader>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Title>Activity</Title>
            <ActivityGrid label="Unaided" value={v.activity.unaided} onChange={(next) => setActivitySection("unaided", next)} />
            <ActivityGrid label="Aided" value={v.activity.aided} onChange={(next) => setActivitySection("aided", next)} showAidedRadios />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Title>Cover Test</Title>
            <CoverTestSection value={v.coverTest} onChange={(ct) => onChange?.({ ...v, coverTest: ct })} />
          </Paper>
        </Grid>

        {/* BINOCULAR VISION */}
        <Grid item xs={12}>
          <SectionHeader>Binocular Vision</SectionHeader>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="NPC"
                  size="small"
                  fullWidth
                  value={v.npc || ""}
                  onChange={(e) => setRootField('npc', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="stereopsis-label">Stereopsis</InputLabel>
                  <Select
                    labelId="stereopsis-label"
                    label="Stereopsis"
                    value={v.stereopsis?.method || ""}
                    onChange={(e) => setStereo('method', e.target.value)}
                  >
                    <MenuItem value="" style={{ display: 'none' }} />
                    <MenuItem value="randot">Randot</MenuItem>
                    <MenuItem value="stereofly">Stereofly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="D"
                  size="small"
                  fullWidth
                  value={v.stereopsis?.d || ""}
                  onChange={(e) => setStereo('d', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="N"
                  size="small"
                  fullWidth
                  value={v.stereopsis?.n || ""}
                  onChange={(e) => setStereo('n', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ADDITIONAL TESTING */}
        <Grid item xs={12}>
          <SectionHeader>Additional Testing</SectionHeader>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Column 1: Color */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Title>Color Vision</Title>
                <Box display="flex" alignItems="center" gap={2} sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Checkbox checked={!!v.color?.ishihara} onChange={(e) => setRootField('color', { ...(v.color || {}), ishihara: e.target.checked })} />}
                    label="Ishihara"
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="color-od-label">OD</InputLabel>
                    <Select
                      labelId="color-od-label"
                      label="OD"
                      value={v.color?.od || ""}
                      onChange={(e) => setRootField('color', { ...(v.color || {}), od: e.target.value })}
                    >
                      <MenuItem value="" style={{ display: 'none' }} />
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="colour-deficiency">Colour Deficiency</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="color-os-label">OS</InputLabel>
                    <Select
                      labelId="color-os-label"
                      label="OS"
                      value={v.color?.os || ""}
                      onChange={(e) => setRootField('color', { ...(v.color || {}), os: e.target.value })}
                    >
                      <MenuItem value="" style={{ display: 'none' }} />
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="colour-deficiency">Colour Deficiency</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Checkbox checked={!!v.color?.both} onChange={(e) => setRootField('color', { ...(v.color || {}), both: e.target.checked })} />}
                    label="Both"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Column 2: AMP / Fields / Amsler table */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Title>Other Tests</Title>
                <Grid container spacing={1} sx={{ mb: 0.5 }}>
                  <Grid item xs={3}></Grid>
                  <Grid item xs={4}><Typography variant="caption" sx={{ color: Colors.dim_grey }}>OD</Typography></Grid>
                  <Grid item xs={5}><Typography variant="caption" sx={{ color: Colors.dim_grey }}>OS</Typography></Grid>
                </Grid>
                {[
                  { key: 'amp', label: 'AMP' },
                  { key: 'fields', label: 'Fields' },
                  { key: 'amsler', label: 'Amsler' },
                ].map((row) => (
                  <Grid key={row.key} container spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Grid item xs={3}><Typography variant="body2">{row.label}</Typography></Grid>
                    <Grid item xs={4}>
                      <TextField
                        size="small"
                        fullWidth
                        value={v.tests?.[row.key]?.od || ""}
                        onChange={(e) => setRootField('tests', { ...(v.tests || {}), [row.key]: { ...(v.tests?.[row.key] || {}), od: e.target.value } })}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        size="small"
                        fullWidth
                        value={v.tests?.[row.key]?.os || ""}
                        onChange={(e) => setRootField('tests', { ...(v.tests || {}), [row.key]: { ...(v.tests?.[row.key] || {}), os: e.target.value } })}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* NOTES */}
        <Grid item xs={12}>
          <SectionHeader>Notes</SectionHeader>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Additional observations or findings..."
              value={v.notes || ""}
              onChange={(e) => setRootField('notes', e.target.value)}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function CoverTestRow({ title, row, onChange }) {
  const handleUnilChange = (next) => onChange?.({ ...row, unil: next });
  const handleAltChange = (next) => onChange?.({ ...row, alt: next });

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: Colors.dim_grey, mb: 0.5, display: 'block' }}>Unilateral</Typography>
        <Box display="flex" alignItems="center" gap={1.5} sx={{ flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ minWidth: 20 }}>H</Typography>
            <TextField size="small" sx={{ width: 100 }} value={row.unil.h} onChange={(e) => handleUnilChange({ ...row.unil, h: e.target.value })} />
            <RadioGroup row value={row.unil?.hDir ?? ""} onChange={(e) => handleUnilChange({ ...row.unil, hDir: e.target.value })}>
              <FormControlLabel value="bo" control={<Radio size="small" />} label="BO" />
              <FormControlLabel value="bi" control={<Radio size="small" />} label="BI" />
            </RadioGroup>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ minWidth: 20 }}>V</Typography>
            <TextField size="small" sx={{ width: 100 }} value={row.unil.v} onChange={(e) => handleUnilChange({ ...row.unil, v: e.target.value })} />
            <RadioGroup row value={row.unil?.vDir ?? ""} onChange={(e) => handleUnilChange({ ...row.unil, vDir: e.target.value })}>
              <FormControlLabel value="bd" control={<Radio size="small" />} label="BD" />
              <FormControlLabel value="bu" control={<Radio size="small" />} label="BU" />
            </RadioGroup>
          </Box>
        </Box>
      </Box>

      <Box sx={{ ml: 0, mb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel control={<Checkbox checked={!!row.ortho} onChange={(e) => onChange?.({ ...row, ortho: e.target.checked })} />} label="Ortho" />
          <FormControlLabel control={<Checkbox checked={!!row.ns} onChange={(e) => onChange?.({ ...row, ns: e.target.checked })} />} label="NS" />
        </Box>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: Colors.dim_grey, mb: 0.5, display: 'block' }}>Alternating</Typography>
        <Box display="flex" alignItems="center" gap={1.5} sx={{ flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ minWidth: 20 }}>H</Typography>
            <TextField size="small" sx={{ width: 100 }} value={row.alt.h} onChange={(e) => handleAltChange({ ...row.alt, h: e.target.value })} />
            <RadioGroup row value={row.alt?.hDir ?? ""} onChange={(e) => handleAltChange({ ...row.alt, hDir: e.target.value })}>
              <FormControlLabel value="bo" control={<Radio size="small" />} label="BO" />
              <FormControlLabel value="bi" control={<Radio size="small" />} label="BI" />
            </RadioGroup>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ minWidth: 20 }}>V</Typography>
            <TextField size="small" sx={{ width: 100}} value={row.alt.v} onChange={(e) => handleAltChange({ ...row.alt, v: e.target.value })} />
            <RadioGroup row value={row.alt?.vDir ?? ""} onChange={(e) => handleAltChange({ ...row.alt, vDir: e.target.value })}>
              <FormControlLabel value="bd" control={<Radio size="small" />} label="BD" />
              <FormControlLabel value="bu" control={<Radio size="small" />} label="BU" />
            </RadioGroup>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function CoverTestSection({ value, onChange }) {
  const v = value || {};
  const setRow = (key, next) => onChange?.({ ...v, [key]: next });
  return (
    <Box>
      <CoverTestRow title="Dist" row={v.dist} onChange={(next) => setRow('dist', next)} />
      <CoverTestRow title="Near" row={v.near} onChange={(next) => setRow('near', next)} />
    </Box>
  );
}
